import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { caseService } from '../services/caseService';
import { projectAPI } from '../services/api';
import { userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface CaseEditForm {
  title: string;
  description: string;
  project_id: string;
  assigned_to: string;
  status: string;
  priority: string;
  type: string;
}

interface Meta {
  statuses: Record<string, string>;
  priorities: Record<string, string>;
  types: Record<string, string>;
}

const CaseEdit: React.FC = () => {
  const { firmId, caseId } = useParams<{ firmId: string; caseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [meta, setMeta] = useState<Meta>({ statuses: {}, priorities: {}, types: {} });
  const [projects, setProjects] = useState<any[]>([]);
  const [assignees, setAssignees] = useState<any[]>([]);
  const [caseData, setCaseData] = useState<any>(null);
  
  const [form, setForm] = useState<CaseEditForm>({
    title: '',
    description: '',
    project_id: '',
    assigned_to: '',
    status: 'open',
    priority: 'normal',
    type: 'new',
  });

  // Load metadata (statuses, priorities, types)
  const loadMeta = async () => {
    try {
      const data = await caseService.getMeta();
      if (data.success) {
        setMeta(data.data);
      }
    } catch (error) {
      console.error('Failed to load metadata:', error);
    }
  };

  // Load projects
  const loadProjects = async () => {
    try {
      const response = await projectAPI.getAll({ firm_id: firmId });
      const data = response.data.data?.data || response.data.data || [];
      setProjects(data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  // Load assignees (users)
  const loadAssignees = async () => {
    try {
      const response = await userAPI.list({ firm_id: Number(firmId), status: 'active' });
      setAssignees(response.data.data || []);
    } catch (error) {
      console.error('Failed to load assignees:', error);
    }
  };

  // Load case data
  const loadCase = async () => {
    if (!caseId) return;
    
    try {
      const response = await caseService.getCaseById(Number(caseId));
      const caseInfo = response.data;
      setCaseData(caseInfo);
      
      setForm({
        title: caseInfo.title || '',
        description: caseInfo.description || '',
        project_id: caseInfo.project_id?.toString() || '',
        assigned_to: caseInfo.assigned_to?.id?.toString() || '',
        status: caseInfo.status || 'open',
        priority: caseInfo.priority || 'normal',
        type: caseInfo.type || 'new',
      });
    } catch (error) {
      console.error('Failed to load case:', error);
      setError('Failed to load case data');
    }
  };

  // Load all data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadMeta(),
          loadProjects(),
          loadAssignees(),
          loadCase()
        ]);
      } catch (error) {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [caseId, firmId]);

  const handleChange = (field: keyof CaseEditForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.title.trim()) {
      setError('Case title is required');
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      const payload = {
        title: form.title,
        description: form.description,
        project_id: form.project_id ? Number(form.project_id) : null,
        assigned_to: form.assigned_to ? Number(form.assigned_to) : null,
        status: form.status,
        priority: form.priority,
        type: form.type,
      };

      await caseService.updateCase(Number(caseId), payload);
      setSuccess('Case updated successfully');
      
      // Navigate back to case view after a short delay
      setTimeout(() => {
        navigate(`/firms/${firmId}/cases/${caseId}`);
      }, 1500);
      
    } catch (error: any) {
      setError(error.message || 'Failed to update case');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/firms/${firmId}/cases/${caseId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link 
          color="inherit" 
          href={`/firms/${firmId}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/firms/${firmId}`);
          }}
        >
          Firm Dashboard
        </Link>
        <Link 
          color="inherit" 
          href={`/firms/${firmId}/cases`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/firms/${firmId}/cases`);
          }}
        >
          Cases
        </Link>
        <Link 
          color="inherit" 
          href={`/firms/${firmId}/cases/${caseId}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/firms/${firmId}/cases/${caseId}`);
          }}
        >
          {caseData?.title || 'Case'}
        </Link>
        <Typography color="text.primary">Edit</Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Edit Case
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Case Title */}
          <TextField
            fullWidth
            label="Case Title"
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
            required
            sx={{ mb: 2 }}
          />

          {/* Case Description */}
          <TextField
            fullWidth
            label="Case Description"
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            multiline
            rows={4}
            sx={{ mb: 3 }}
          />

          {/* Task Information Section */}
          <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
            Task Information
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
            {/* Project */}
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                value={form.project_id}
                onChange={(e) => handleChange('project_id', e.target.value)}
                label="Project"
              >
                <MenuItem value="">
                  <em>No Project</em>
                </MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id.toString()}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Assigned To */}
            <FormControl fullWidth>
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={form.assigned_to}
                onChange={(e) => handleChange('assigned_to', e.target.value)}
                label="Assigned To"
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {assignees.map((assignee) => (
                  <MenuItem key={assignee.id} value={assignee.id.toString()}>
                    {assignee.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
            {/* Status */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
                label="Status"
              >
                {Object.entries(meta.statuses).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Priority */}
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={form.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                label="Priority"
              >
                {Object.entries(meta.priorities).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Type */}
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.type}
                onChange={(e) => handleChange('type', e.target.value)}
                label="Type"
              >
                {Object.entries(meta.types).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : 'Update Case'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default CaseEdit;
